import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import { Product } from '../../types/models';
import { productService } from '../../services/firebase';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) {
        setError('Product ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const products = await productService.getProducts();
        const foundProduct = products.find(p => p.id === id);
        
        if (foundProduct) {
          setProduct(foundProduct);
        } else {
          setError('Product not found');
        }
      } catch (error) {
        console.error('Error loading product:', error);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  const handleBack = () => {
    navigate('/products');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          variant="outlined"
          sx={{ mb: 2, borderRadius: 2 }}
        >
          Back to Products
        </Button>
        <Alert severity="error">{error || 'Product not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Back to Products
        </Button>
      </Box>

      {/* Product Details Card */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {product.name}
            </Typography>
            <Chip
              label={product.isAvailable ? 'Available' : 'Unavailable'}
              color={product.isAvailable ? 'success' : 'error'}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {product.description}
          </Typography>

          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Category</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{product.category}</Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">Price per {product.unit}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                ₹{product.price}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">Unit</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{product.unit}</Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">Organic</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {product.isOrganic ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Box>

          {product.images && product.images.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Product Images</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {product.images.map((image, index) => (
                  <Box key={index} sx={{ width: 200, height: 200 }}>
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 8
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary">
            Created: {product.createdAt?.toLocaleDateString()} • 
            Updated: {product.updatedAt?.toLocaleDateString()}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductDetail;
